import { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { PageLayout, StyledBreadcrumb } from "common-fe";
import { useOrganizationMembers } from "../hooks/useOrganizationMembers";
import { useActiveOrgRole } from "../hooks/useActiveOrgRole";
import MemberRolesDialog from "../components/MemberRolesDialog";
import IconConfirmButton from "../components/IconConfirmButton";

// "Lastname, Firstname" when we have names, else the email (so a row is never blank).
const memberName = (m) => {
  const last = (m.lastName || "").trim();
  const first = (m.firstName || "").trim();
  if (last || first) return [last, first].filter(Boolean).join(", ");
  return m.email || m.userId;
};

const byLastName = (a, b) =>
  memberName(a).localeCompare(memberName(b), undefined, { sensitivity: "base" });

export default function OrgAdmin() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const auth = useAuth();
  const selfId = auth.user?.profile?.sub;

  const { activeOrgId, activeOrg, isOwner, canManageMembers } = useActiveOrgRole();
  const {
    members,
    loading,
    error,
    nextCursor,
    loadingMore,
    loadMore,
    lookupByEmail,
    addMember,
    removeMember,
    updateMemberPermissions,
    refetchSilent,
  } = useOrganizationMembers(activeOrgId);

  // Large org (>20, i.e. more than one page): search server-side instead of
  // filtering the loaded page. Small orgs (the majority) filter client-side.
  const isLargeOrg = !!nextCursor;

  const [query, setQuery] = useState("");
  const [serverResults, setServerResults] = useState(null); // null = not searched
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [rolesMember, setRolesMember] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Small-org rows: client-side filter over the fully-loaded page.
  const clientRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? members.filter((m) =>
          [m.firstName, m.lastName, m.email]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q))
        )
      : members;
    return [...filtered].sort(byLastName);
  }, [members, query]);

  const sortedMembers = useMemo(() => [...members].sort(byLastName), [members]);

  // Large orgs: while a server search is active show its results, else the page.
  const rows = isLargeOrg
    ? serverResults !== null
      ? serverResults
      : sortedMembers
    : clientRows;

  const searchActive = isLargeOrg
    ? serverResults !== null
    : query.trim() !== "";

  // Server-side search for large orgs — exact email lookup (the indexed,
  // bounded operation). Empty query clears back to the first page.
  const runServerSearch = async () => {
    const q = query.trim();
    setSearchError(null);
    if (!q) {
      setServerResults(null);
      return;
    }
    setSearching(true);
    try {
      const m = await lookupByEmail(q);
      setServerResults(m ? [m] : []);
    } catch (err) {
      setSearchError(err.message);
      setServerResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onSearchChange = (value) => {
    setQuery(value);
    // In large mode, clearing the box returns to the first page immediately.
    if (isLargeOrg && value.trim() === "") setServerResults(null);
  };

  // In the Org section the leading crumb is "Organization" (the section), not the
  // app-home "Apps". On this page (the section root) it's the current crumb.
  const breadcrumbs = [
    <StyledBreadcrumb key="org" label={t("orgAdmin.breadcrumb")} />,
  ];

  if (!activeOrgId) {
    return (
      <PageLayout maxWidth={900}>
        <Alert severity="info">{t("orgAdmin.noActiveOrg")}</Alert>
      </PageLayout>
    );
  }

  const submitAdd = async () => {
    setAddError(null);
    setAdding(true);
    try {
      await addMember(addEmail.trim());
      setAddOpen(false);
      setAddEmail("");
      refetchSilent();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (userId) => {
    setActionError(null);
    try {
      await removeMember(userId);
      refetchSilent();
    } catch (err) {
      setActionError(err.message);
    }
  };

  // Owner toggle lives in the MemberRolesDialog; it surfaces its own errors, so
  // this rethrows (after refreshing the list) rather than swallowing.
  const onOwnerChange = async (userId, checked) => {
    await updateMemberPermissions(userId, checked);
    refetchSilent();
  };

  return (
    <PageLayout maxWidth={900} breadcrumbs={breadcrumbs}>
      {/* Header: org name + page-level actions (Add member, settings gear) —
          kept here, away from the search field, so Add never reads as the
          search submit. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: "text.primary" }}>
            {activeOrg?.name || t("orgAdmin.untitledOrg")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("orgAdmin.membersSubtitle")}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          {canManageMembers && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              data-testid="add-member-button"
              onClick={() => setAddOpen(true)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("orgAdmin.addMember")}
            </Button>
          )}
          {isOwner && (
          <Tooltip title={t("orgAdmin.settingsTooltip")}>
            <IconButton
              data-testid="org-settings-gear"
              onClick={() => navigate("/orgs/settings")}
              sx={{ color: "text.primary" }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

      {/* Search — a modest field (with a Find button only for large, server-side
          orgs). The Add action lives in the header, not here. */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder={
            isLargeOrg
              ? t("orgAdmin.searchEmailPlaceholder")
              : t("orgAdmin.searchPlaceholder")
          }
          value={query}
          data-testid="member-search"
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (isLargeOrg && e.key === "Enter") runServerSearch();
          }}
          sx={{ width: "100%", maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {isLargeOrg && (
          <Button
            variant="outlined"
            disabled={searching}
            data-testid="member-search-go"
            onClick={runServerSearch}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("orgAdmin.find")}
          </Button>
        )}
      </Stack>

      <Table size="small" data-testid="members-table" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "38%" }}>{t("orgAdmin.colName")}</TableCell>
            <TableCell sx={{ width: "42%" }}>{t("orgAdmin.colEmail")}</TableCell>
            <TableCell align="center" sx={{ width: "10%" }} />
            <TableCell align="center" sx={{ width: "10%" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            [0, 1, 2].map((i) => (
              <TableRow key={i}>
                <TableCell colSpan={4}><Skeleton height={32} /></TableCell>
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
                  {searchActive ? t("orgAdmin.noMatches") : t("orgAdmin.noMembers")}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((m) => {
              const isSelf = m.userId === selfId;
              return (
                <TableRow key={m.userId} data-testid={`member-row-${m.userId}`}>
                  <TableCell sx={{ color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {memberName(m)}
                    {m.owner && (
                      <Chip
                        size="small"
                        label={t("orgAdmin.ownerTag")}
                        sx={{ ml: 1 }}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell title={m.email} sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.email}
                  </TableCell>
                  {/* Manage roles — own column, icon button */}
                  <TableCell align="center" sx={{ p: 0.5 }}>
                    {canManageMembers && (
                      <Tooltip title={t("organizations.members.manageRolesButton")}>
                        <IconButton
                          size="small"
                          data-testid={`manage-roles-${m.userId}`}
                          aria-label={t("organizations.members.manageRolesButton")}
                          onClick={() => setRolesMember(m)}
                        >
                          <ManageAccountsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {/* Remove — own column, click-to-confirm icon */}
                  <TableCell align="center" sx={{ p: 0.5 }}>
                    {canManageMembers && !isSelf && !m.owner && (
                      <IconConfirmButton
                        onConfirm={() => onRemove(m.userId)}
                        tooltip={t("organizations.members.removeButton")}
                        confirmTooltip={t("organizations.members.removeConfirm")}
                        cancelTooltip={t("organizations.members.removeCancel")}
                        buttonTestId={`remove-member-${m.userId}`}
                        confirmTestId={`remove-member-confirm-${m.userId}`}
                        cancelTestId={`remove-member-cancel-${m.userId}`}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {isLargeOrg && !searchActive && (
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
          {t("orgAdmin.largeOrgHint")}
        </Typography>
      )}

      {isLargeOrg && serverResults === null && nextCursor && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button onClick={loadMore} disabled={loadingMore} data-testid="load-more-members">
            {loadingMore ? t("organizations.members.loadingMore") : t("organizations.members.loadMore")}
          </Button>
        </Box>
      )}

      {/* Add-member modal */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("orgAdmin.addDialogTitle")}</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField
            fullWidth
            margin="normal"
            type="email"
            label={t("orgAdmin.emailLabel")}
            value={addEmail}
            data-testid="add-member-email"
            onChange={(e) => setAddEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t("orgAdmin.cancel")}</Button>
          <Button
            variant="contained"
            disabled={adding || !addEmail.trim()}
            data-testid="add-member-submit"
            onClick={submitAdd}
          >
            {t("orgAdmin.add")}
          </Button>
        </DialogActions>
      </Dialog>

      <MemberRolesDialog
        orgId={activeOrgId}
        member={rolesMember}
        open={!!rolesMember}
        onClose={() => setRolesMember(null)}
        onChanged={refetchSilent}
        canManageOwner={isOwner && !!rolesMember && rolesMember.userId !== selfId}
        onOwnerChange={(checked) => onOwnerChange(rolesMember.userId, checked)}
      />
    </PageLayout>
  );
}
